/**
 * Score presentation components.
 *
 * The governing rule (docs/03-design-system.md §2): a score is **never** communicated by
 * colour alone, and never by a bare number. Every score renders as
 * colour + numeral + band label + bar/arc length, and exposes a composed screen-reader
 * label. `withheld` is a first-class visual state, because refusing to judge is a
 * deliberate product behaviour rather than an error.
 */

import { bandOf, clamp, type ScoreBand, type ScoreStatus } from '@saysooner/core';
import type { JSX, ReactNode } from 'react';
import { useState } from 'react';

export interface ScoreRingProps {
  /** `null` renders the withheld state. */
  readonly score: number | null;
  readonly status?: ScoreStatus | undefined;
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  readonly label?: string | undefined;
  /** Suppresses the reveal animation. Honoured automatically under reduced motion. */
  readonly animate?: boolean | undefined;
}

const RING_SIZES: Record<NonNullable<ScoreRingProps['size']>, number> = {
  sm: 56,
  md: 88,
  lg: 132,
};

export function ScoreRing({
  score,
  status = 'scored',
  size = 'md',
  label,
  animate = true,
}: ScoreRingProps): JSX.Element {
  const px = RING_SIZES[size];
  const radius = (px - 10) / 2;
  const circumference = 2 * Math.PI * radius;

  const withheld = score === null || status === 'withheld' || status === 'unavailable';
  const value = withheld ? 0 : clamp(score, 0, 100);
  const band = withheld ? null : bandOf(value);

  const accessibleLabel = withheld
    ? `${label ?? 'Score'}: not scored. We couldn't hear that clearly.`
    : `${label ?? 'Score'}: ${Math.round(value)} out of 100, ${band?.label}.`;

  return (
    <div
      className={`vl-score-ring vl-score-ring--${size}`}
      role="img"
      aria-label={accessibleLabel}
      data-status={withheld ? 'withheld' : 'scored'}
      data-band={band?.id ?? 'unknown'}
    >
      <svg width={px} height={px} viewBox={`0 0 ${px} ${px}`} aria-hidden="true">
        <circle
          cx={px / 2}
          cy={px / 2}
          r={radius}
          className="vl-score-ring__track"
          fill="none"
          strokeWidth={8}
        />
        {!withheld && (
          <circle
            cx={px / 2}
            cy={px / 2}
            r={radius}
            className="vl-score-ring__value"
            fill="none"
            strokeWidth={8}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - value / 100)}
            transform={`rotate(-90 ${px / 2} ${px / 2})`}
            data-animate={animate ? 'true' : 'false'}
          />
        )}
      </svg>
      <div className="vl-score-ring__content" aria-hidden="true">
        {withheld ? (
          <span className="vl-score-ring__dash">—</span>
        ) : (
          <>
            <span className="vl-score-ring__numeral">{Math.round(value)}</span>
            <span className="vl-score-ring__band">{band?.label}</span>
          </>
        )}
      </div>
    </div>
  );
}

export interface ScoreBarProps {
  readonly label: string;
  readonly score: number | null;
  /** Shown beside the bar, e.g. "+4 this week". */
  readonly delta?: number | undefined;
}

export function ScoreBar({ label, score, delta }: ScoreBarProps): JSX.Element {
  const withheld = score === null;
  const value = withheld ? 0 : clamp(score, 0, 100);
  const band = withheld ? null : bandOf(value);

  return (
    <div className="vl-score-bar" data-band={band?.id ?? 'unknown'}>
      {/* One coherent sentence for assistive tech, rather than a group boundary plus
          three disconnected fragments. */}
      <span className="vl-visually-hidden">
        {withheld
          ? `${label}: not scored.`
          : // The movement is part of the reading, not decoration beside it: a sighted
            // learner sees the arrow, so a screen-reader one hears the same thing.
            `${label}: ${Math.round(value)} out of 100, ${band?.label}.${
              delta !== undefined && Math.round(delta) !== 0
                ? ` ${delta > 0 ? 'Up' : 'Down'} ${Math.abs(Math.round(delta))} since your average.`
                : ''
            }`}
      </span>
      <div className="vl-score-bar__header" aria-hidden="true">
        <span className="vl-score-bar__label">{label}</span>
        <span className="vl-score-bar__numeral">
          {withheld ? '—' : Math.round(value)}
          {delta !== undefined && delta !== 0 && (
            <span className="vl-score-bar__delta" data-direction={delta > 0 ? 'up' : 'down'}>
              {delta > 0 ? '▲' : '▼'} {Math.abs(Math.round(delta))}
            </span>
          )}
        </span>
      </div>
      <div className="vl-score-bar__track" aria-hidden="true">
        <div className="vl-score-bar__fill" style={{ width: `${value}%` }} />
      </div>
      {/* The band word is part of the signal, not decoration. */}
      <span className="vl-score-bar__band" aria-hidden="true">
        {withheld ? 'Not scored' : band?.label}
      </span>
    </div>
  );
}

export interface WithheldNoticeProps {
  readonly reason:
    | 'too_quiet'
    | 'too_short'
    | 'no_speech'
    | 'clipped'
    | 'engines_disagree'
    | 'low_engine_confidence'
    | null;
  readonly message: string | null;
  readonly onRetry?: (() => void) | undefined;
}

/**
 * The withheld state. Phrased as our limitation, never the learner's error — this is the
 * visible half of confidence gating (strategy wedge W2).
 */
export function WithheldNotice({ reason, message, onRetry }: WithheldNoticeProps): JSX.Element {
  const fallback = "I couldn't hear that clearly.";
  return (
    <div className="vl-withheld" role="status" data-reason={reason ?? 'unknown'}>
      <p className="vl-withheld__message">{message ?? fallback}</p>
      {onRetry !== undefined && (
        <button type="button" className="vl-button vl-button--secondary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export interface WordScoreTextProps {
  readonly words: readonly { word: string; score: number; band: ScoreBand }[];
  readonly onWordClick?: ((index: number) => void) | undefined;
}

/** Per-word underlines over the learner's own sentence. Each word is individually tappable. */
export function WordScoreText({ words, onWordClick }: WordScoreTextProps): JSX.Element {
  if (words.length === 0) return <span className="vl-word-score" />;

  return (
    <p className="vl-word-score">
      {words.map((word, index) => (
        <button
          // biome-ignore lint/suspicious/noArrayIndexKey: a word can repeat in one sentence, so position is part of its identity; the list never reorders.
          key={`${word.word}-${index}`}
          type="button"
          className="vl-word-score__word"
          data-band={word.band.id}
          aria-label={`${word.word}: ${Math.round(word.score)} out of 100, ${word.band.label}. Tap to practise.`}
          onClick={onWordClick === undefined ? undefined : () => onWordClick(index)}
          disabled={onWordClick === undefined}
        >
          {word.word}
        </button>
      ))}
    </p>
  );
}

export interface DimensionsPanelProps {
  readonly dimensions: {
    /** Null where the dimension was not measured; ScoreBar renders it as "not scored". */
    readonly accuracy: number | null;
    readonly fluency: number | null;
    readonly completeness: number | null;
    readonly intonation: number | null;
    readonly confidence: number | null;
  } | null;
  readonly deltas?: Partial<Record<string, number>> | undefined;
}

/**
 * The five reported dimensions. `Confidence` is included deliberately: it is the one our
 * primary learner cares about most, and no competitor computes it.
 */
export function DimensionsPanel({ dimensions, deltas = {} }: DimensionsPanelProps): JSX.Element {
  const rows: readonly {
    key: keyof NonNullable<DimensionsPanelProps['dimensions']>;
    label: string;
  }[] = [
    { key: 'accuracy', label: 'Accuracy' },
    { key: 'fluency', label: 'Fluency' },
    { key: 'completeness', label: 'Completeness' },
    { key: 'intonation', label: 'Intonation' },
    { key: 'confidence', label: 'Confidence' },
  ];

  return (
    <div className="vl-dimensions">
      {rows.map((row) => (
        <ScoreBar
          key={row.key}
          label={row.label}
          score={dimensions === null ? null : dimensions[row.key]}
          {...(deltas[row.key] !== undefined ? { delta: deltas[row.key] as number } : {})}
        />
      ))}
    </div>
  );
}

export interface PhonemeRow {
  readonly expected: string;
  readonly produced: string | null;
  readonly syllable: string | null;
  readonly score: number;
  readonly isError: boolean;
  readonly message: string;
  readonly diagnosis: {
    readonly name: string;
    readonly explanation: string;
    readonly remedy: string;
    /**
     * The pair this sound is confusable with, where we know it.
     *
     * The server computes it, all three clients decode it, and until now not one of
     * them showed it — so the card could say "sounded like /j/" and offer no way to go
     * hear the difference, which is the whole method behind minimal-pair practice.
     */
    readonly minimalPairContrast?: readonly [string, string] | null;
  } | null;
}

export interface PhonemeTableProps {
  readonly word: string;
  readonly score: number;
  readonly stressCorrect: boolean | null;
  readonly phonemes: readonly PhonemeRow[];
  readonly onPlayCoach?: ((phoneme: string) => void) | undefined;
  readonly onPractise?: (() => void) | undefined;
  readonly onExplain?: ((phoneme: string) => void) | undefined;
  /** Opens minimal-pair practice for a contrast the learner just confused. */
  readonly onCompare?: ((phoneme: string) => void) | undefined;
  /** Hidden entirely when the gate did not permit phoneme claims. */
  readonly available: boolean;
}

/**
 * The phoneme table — a real `<table>` with headers, so a screen reader can navigate it.
 * Rendered only when the confidence gate permitted phoneme-level claims; otherwise the
 * caller shows sentence-level scores alone.
 */
export function PhonemeTable({
  word,
  score,
  stressCorrect,
  phonemes,
  onPlayCoach,
  onPractise,
  onExplain,
  onCompare,
  available,
}: PhonemeTableProps): JSX.Element | null {
  if (!available || phonemes.length === 0) return null;
  const band = bandOf(score);

  return (
    <section className="vl-phoneme" aria-label={`Sound breakdown for ${word}`}>
      <header className="vl-phoneme__header">
        <span className="vl-phoneme__word">{word}</span>
        <span className="vl-phoneme__score" data-band={band.id}>
          {Math.round(score)}
          <span className="vl-phoneme__band"> · {band.label}</span>
        </span>
      </header>

      {stressCorrect !== null && (
        <p className="vl-phoneme__stress" data-correct={stressCorrect ? 'true' : 'false'}>
          {stressCorrect
            ? 'You stressed the right syllable'
            : 'The stress landed on the wrong syllable'}
        </p>
      )}

      <table className="vl-phoneme__table">
        <thead>
          <tr>
            <th scope="col">Syllable</th>
            <th scope="col">Sound</th>
            <th scope="col">Feedback</th>
            <th scope="col">
              <span className="vl-visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {phonemes.map((row, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a phoneme recurs within a word, so position is part of its identity; the list never reorders.
            <tr key={`${row.expected}-${index}`} data-error={row.isError ? 'true' : 'false'}>
              <td>{row.syllable ?? '—'}</td>
              <td>
                <code className="vl-ipa">/{row.expected}/</code>
              </td>
              <td>
                {row.message}
                {row.diagnosis !== null && (
                  <span className="vl-phoneme__diagnosis"> — {row.diagnosis.explanation}</span>
                )}
              </td>
              <td className="vl-phoneme__actions">
                {onPlayCoach !== undefined && (
                  <button
                    type="button"
                    className="vl-icon-button"
                    aria-label={`Hear /${row.expected}/`}
                    onClick={() => onPlayCoach(row.expected)}
                  >
                    ▶
                  </button>
                )}
                {row.isError && onExplain !== undefined && (
                  <button
                    type="button"
                    className="vl-button vl-button--ghost vl-button--sm"
                    onClick={() => onExplain(row.expected)}
                  >
                    Explain my mistake
                  </button>
                )}
                {/* Only where we know the pair. A "compare" button with nothing to
                    compare against would be the same empty promise as the symbol
                    table nobody could play. */}
                {row.isError &&
                  onCompare !== undefined &&
                  row.diagnosis?.minimalPairContrast != null && (
                    <button
                      type="button"
                      className="vl-button vl-button--ghost vl-button--sm"
                      onClick={() => onCompare(row.expected)}
                    >
                      Compare /{row.diagnosis.minimalPairContrast[0]}/ and /
                      {row.diagnosis.minimalPairContrast[1]}/
                    </button>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {onPractise !== undefined && (
        <button type="button" className="vl-button vl-button--secondary" onClick={onPractise}>
          Practise this word
        </button>
      )}
    </section>
  );
}

export interface RemediationCardProps {
  readonly phonemes: readonly string[];
  readonly estimatedMinutes: number;
  readonly projectedAccuracyGain: number;
  readonly onStart: () => void;
}

/**
 * Bounded, quantified remediation. The cap of three sounds is the point: an unbounded
 * list of everything wrong is what makes learners quit.
 */
export function RemediationCard({
  phonemes,
  estimatedMinutes,
  projectedAccuracyGain,
  onStart,
}: RemediationCardProps): JSX.Element | null {
  if (phonemes.length === 0) return null;

  const count = phonemes.length;
  return (
    <button type="button" className="vl-remediation" onClick={onStart}>
      <span className="vl-remediation__headline">
        Practise {count} {count === 1 ? 'sound' : 'sounds'} to raise Accuracy +
        {projectedAccuracyGain.toFixed(1)}%
      </span>
      <span className="vl-remediation__detail">
        {phonemes.map((p) => `/${p}/`).join(' · ')} · about {estimatedMinutes}{' '}
        {estimatedMinutes === 1 ? 'minute' : 'minutes'}
      </span>
    </button>
  );
}

export interface AppealControlProps {
  /**
   * Files the appeal. `consentToStore` decides only whether we keep the recording:
   * the appeal itself is filed either way, which is what the second step says out loud.
   */
  readonly onAppeal: (consentToStore: boolean) => void;
  readonly appealed: boolean;
  readonly onReport?: (() => void) | undefined;
  /** The server's own words once it has answered, so we do not paraphrase its answer. */
  readonly outcome?: string | undefined;
}

/**
 * "I said that right." Quiet by design, but always present on a scored utterance. It
 * withdraws the mistake immediately and feeds our eval corpus — the single most
 * strategically important control in the product.
 *
 * Storing the recording is asked separately, at the moment of the appeal, because that
 * is the moment the learner is deciding whether we may keep their voice. The appeal is
 * filed either way: a learner who declines must not be left thinking they were ignored,
 * which is the opposite of what this control exists to fix.
 */
export function AppealControl({
  onAppeal,
  appealed,
  onReport,
  outcome,
}: AppealControlProps): JSX.Element {
  const [asking, setAsking] = useState(false);

  if (appealed) {
    return (
      <p className="vl-appeal vl-appeal--done" role="status">
        {outcome ?? 'Thanks — we’ve taken that back.'}
      </p>
    );
  }

  if (asking) {
    return (
      <div className="vl-appeal vl-appeal--consent">
        <p className="vl-appeal__question">
          May we keep this recording to check our scoring? Either way, we are taking the mistake
          back.
        </p>
        <button
          type="button"
          className="vl-button vl-button--secondary vl-button--sm"
          onClick={() => onAppeal(true)}
        >
          Yes, keep it
        </button>
        <button
          type="button"
          className="vl-button vl-button--ghost vl-button--sm"
          onClick={() => onAppeal(false)}
        >
          No, just take it back
        </button>
      </div>
    );
  }

  return (
    <div className="vl-appeal">
      <button
        type="button"
        className="vl-button vl-button--ghost vl-button--sm"
        onClick={() => setAsking(true)}
      >
        I said that right
      </button>
      {onReport !== undefined && (
        <button
          type="button"
          className="vl-button vl-button--ghost vl-button--sm"
          onClick={onReport}
        >
          Report bad feedback
        </button>
      )}
    </div>
  );
}

export interface PlaybackRowProps {
  readonly onPlayLearner?: (() => void) | undefined;
  readonly onPlayCoach?: (() => void) | undefined;
  readonly onPlayCoachSlow?: (() => void) | undefined;
}

/** Your recording versus the model, including a slow model — BoldVoice's best idea. */
export function PlaybackRow({
  onPlayLearner,
  onPlayCoach,
  onPlayCoachSlow,
}: PlaybackRowProps): JSX.Element {
  return (
    <div className="vl-playback">
      {onPlayCoach !== undefined && (
        <button
          type="button"
          className="vl-button vl-button--secondary vl-button--sm"
          onClick={onPlayCoach}
        >
          ▶ Coach
        </button>
      )}
      {onPlayCoachSlow !== undefined && (
        <button
          type="button"
          className="vl-button vl-button--secondary vl-button--sm"
          onClick={onPlayCoachSlow}
          aria-label="Hear the coach slowly"
        >
          🐌 Slow
        </button>
      )}
      {onPlayLearner !== undefined && (
        <button
          type="button"
          className="vl-button vl-button--secondary vl-button--sm"
          onClick={onPlayLearner}
        >
          ▶ You
        </button>
      )}
    </div>
  );
}

export interface NaturalAlternativeProps {
  readonly text: string;
  readonly onPlay?: (() => void) | undefined;
  readonly onPractise?: (() => void) | undefined;
}

/** "Say it more naturally" — an idiomatic rephrasing, not a correction. */
export function NaturalAlternative({
  text,
  onPlay,
  onPractise,
}: NaturalAlternativeProps): JSX.Element {
  return (
    <section className="vl-natural" aria-label="A more natural way to say it">
      <h4 className="vl-natural__title">Say it more naturally</h4>
      <p className="vl-natural__text" lang="und">
        {text}
      </p>
      <PlaybackRow {...(onPlay !== undefined ? { onPlayCoach: onPlay } : {})} />
      {onPractise !== undefined && (
        <button
          type="button"
          className="vl-button vl-button--ghost vl-button--sm"
          onClick={onPractise}
        >
          Try this one
        </button>
      )}
    </section>
  );
}

export function VisuallyHidden({ children }: { readonly children: ReactNode }): JSX.Element {
  return <span className="vl-visually-hidden">{children}</span>;
}
